import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  Alert
} from '@mui/material';
import { Email } from '@mui/icons-material';
import apiService from '../../services/api';
import AuthPageLayout, { authTextFieldSx, authSubmitButtonSx, authTextLinkSx } from './AuthPageLayout';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await apiService.forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(err.message || 'Unable to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout
      compact
      title="Forgot password"
      subtitle={sent
        ? 'Check your email for the next step.'
        : 'Enter your email and we will send a reset link if an account exists.'}
    >
      {(isMobile) => (
        sent ? (
          <Box sx={{ width: '100%' }}>
            <Alert
              severity="success"
              sx={{
                mb: 3,
                background: 'rgba(76, 175, 80, 0.12)',
                border: '1px solid rgba(76, 175, 80, 0.35)',
                color: 'var(--color-text)',
                '& .MuiAlert-icon': { color: 'var(--color-success, #4caf50)' }
              }}
            >
              If an account exists for that email, we sent a password reset link. It expires in 60 minutes.
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button component={Link} to="/login" variant="text" sx={authTextLinkSx(isMobile)}>
                Back to sign in
              </Button>
            </Box>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  background: 'rgba(244, 67, 54, 0.1)',
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  color: 'var(--color-text)'
                }}
              >
                {error}
              </Alert>
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" style={{ color: 'var(--color-text-secondary)' }} />
                  </InputAdornment>
                ),
                style: {
                  fontSize: isMobile ? '16px' : '14px',
                  minHeight: isMobile ? '48px' : '52px'
                }
              }}
              sx={authTextFieldSx(isMobile)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !email.trim()}
              sx={authSubmitButtonSx(isMobile)}
            >
              {loading ? (
                <CircularProgress size={isMobile ? 24 : 20} sx={{ color: 'var(--color-text)' }} />
              ) : (
                'Send reset link'
              )}
            </Button>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button component={Link} to="/login" variant="text" sx={authTextLinkSx(isMobile)}>
                Back to sign in
              </Button>
            </Box>
          </Box>
        )
      )}
    </AuthPageLayout>
  );
};

export default ForgotPasswordPage;
